/**
 * BodyEditor — UI for the "Then..." section of the Logic Wizard.
 *
 * The body of an IF is what runs when the premise is true: nothing, a
 * TERMINATE, a SKIP TO a different question, a brand-new question stub, or
 * a BEGIN/END block of arbitrary statements.
 */
import React from "react"
import { Radio, Input, Select } from "antd"
import { LabelItem, LabelType } from "utils/labelUtils"
import {
  BodyAction,
  BODY_QUESTION_TYPES,
} from "utils/logicWizard/bodyAction"
import { isReservedWord } from "utils/logicWizard/grammar"

interface BodyEditorProps {
  action: BodyAction
  onChange: (next: BodyAction) => void
  /** All labels — used to populate the SKIP TO target picker. */
  labels: LabelItem[]
}

const BodyEditor: React.FC<BodyEditorProps> = ({
  action,
  onChange,
  labels,
}) => {
  const questionLabels = labels
    .filter((l) => l.type === LabelType.QUESTION)
    .filter((l) => !isReservedWord(l.name))

  const handleKindChange = (kind: BodyAction["kind"]) => {
    switch (kind) {
      case "none":
        onChange({ kind: "none" })
        return
      case "terminate":
        onChange({ kind: "terminate" })
        return
      case "skip-to":
        onChange({ kind: "skip-to", targetLabel: "" })
        return
      case "question":
        onChange({
          kind: "question",
          label: "",
          text: "",
          type: "SELECT ONE",
        })
        return
      case "begin-end":
        onChange({ kind: "begin-end", innerText: "" })
        return
    }
  }

  return (
    <div className="border rounded p-3 bg-blue-50 mt-3">
      <div className="text-sm font-medium mb-2">Then…</div>
      <Radio.Group
        value={action.kind}
        onChange={(e) => handleKindChange(e.target.value)}
      >
        <div className="flex flex-col gap-2">
          <Radio value="none">
            <span>
              Just the condition
              <span className="text-xs text-gray-500 ml-2">
                (no body — write the next statement yourself)
              </span>
            </span>
          </Radio>

          <Radio value="terminate">
            <span>
              Terminate the survey
              <span className="text-xs text-gray-500 ml-2">
                (ends the interview when the condition is true)
              </span>
            </span>
          </Radio>

          <Radio value="skip-to">Skip to question…</Radio>
          {action.kind === "skip-to" && (
            <div className="ml-6">
              <Select
                showSearch
                placeholder="Pick the target question…"
                value={action.targetLabel || undefined}
                onChange={(label) =>
                  onChange({ kind: "skip-to", targetLabel: label })
                }
                style={{ minWidth: 280 }}
                options={questionLabels.map((q) => ({
                  value: q.name,
                  label: q.name,
                }))}
              />
            </div>
          )}

          <Radio value="question">Show a new question</Radio>
          {action.kind === "question" && (
            <div className="ml-6 flex flex-col gap-2">
              <Input
                placeholder="Question label (e.g. NEWQ1)"
                value={action.label}
                onChange={(e) =>
                  onChange({ ...action, label: e.target.value })
                }
              />
              <Input
                placeholder="Question text (e.g. What is your favourite colour?)"
                value={action.text}
                onChange={(e) =>
                  onChange({ ...action, text: e.target.value })
                }
              />
              <Select
                value={action.type}
                onChange={(t) => onChange({ ...action, type: t })}
                options={BODY_QUESTION_TYPES.map((t) => ({
                  value: t,
                  label: t,
                }))}
                style={{ minWidth: 200 }}
              />
              <span className="text-xs text-gray-500">
                A template will be inserted; fill in answer options
                afterwards in the editor.
              </span>
            </div>
          )}

          <Radio value="begin-end">BEGIN/END block</Radio>
          {action.kind === "begin-end" && (
            <div className="ml-6">
              <Input.TextArea
                value={action.innerText}
                onChange={(e) =>
                  onChange({ kind: "begin-end", innerText: e.target.value })
                }
                placeholder={
                  "One statement per line, e.g.\n" +
                  "TERMINATE\n" +
                  "or another IF, SKIP TO, etc."
                }
                rows={4}
              />
            </div>
          )}
        </div>
      </Radio.Group>
    </div>
  )
}

export default BodyEditor
